import {
  AUDIT_ACTIONS,
  AUDIT_ENTITY_TYPES,
  type AuditAction,
  type AuditEntityType,
  type AuditLogEntry,
} from '@/shared/api/audit';
import { parseIsoDate } from '@/modules/dashboards/dashboardRanges';

/**
 * `validateSearch` of `/admin/audit` (`13 §4.12`), kept apart from the page so neither file
 * exports anything but a component (`reportSearch.ts`).
 */

/** `validateSearch`: every filter lives in the URL, so the trail survives a reload and is shareable. */
export interface AuditLogSearch {
  entityType?: AuditEntityType;
  entityId?: string;
  action?: AuditAction;
  actorUserId?: string;
  from?: string;
  to?: string;
}

function parseFreeText(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** A value the collection would refuse (`07 §1.8`) is dropped instead of thrown on, like every other search schema. */
export function auditLogSearchSchema(search: Record<string, unknown>): AuditLogSearch {
  const result: AuditLogSearch = {};

  const entityType = search['entityType'];
  if (
    typeof entityType === 'string' &&
    AUDIT_ENTITY_TYPES.includes(entityType as AuditEntityType)
  ) {
    result.entityType = entityType as AuditEntityType;
  }

  const entityId = parseFreeText(search['entityId']);
  if (entityId !== undefined) {
    result.entityId = entityId;
  }

  const action = search['action'];
  if (typeof action === 'string' && AUDIT_ACTIONS.includes(action as AuditAction)) {
    result.action = action as AuditAction;
  }

  const actorUserId = parseFreeText(search['actorUserId']);
  if (actorUserId !== undefined) {
    result.actorUserId = actorUserId;
  }

  // Both ends travel together: a lone `from` or `to` is not a range (`dashboardSearchSchema`).
  const from = parseIsoDate(search['from']);
  const to = parseIsoDate(search['to']);
  if (from !== undefined && to !== undefined) {
    result.from = from;
    result.to = to;
  }

  return result;
}

/**
 * Where a row leads (`13 §4.12`): the routes this console actually has, already interpolated
 * (`ListRow`'s own convention); `undefined` means the row stays plain text.
 */
export function auditEntityHref(
  entry: Pick<AuditLogEntry, 'entityType' | 'entityId' | 'action'>,
): string | undefined {
  switch (entry.entityType) {
    case 'ticket':
      return `/tickets/${entry.entityId}`;
    case 'asset':
      // The baja is logical and the card of a deleted asset is "not found": its row stays text.
      return entry.action === 'delete' ? undefined : `/assets/${entry.entityId}`;
    case 'invoice':
      return `/billing/invoices/${entry.entityId}`;
    case 'contract':
      return `/billing/contracts/${entry.entityId}`;
    case 'billing_period':
      return `/billing/periods/${entry.entityId}`;
    case 'user':
      // No per-user route exists yet: the whole directory is where the trail's own row leads.
      return '/admin/users';
    case 'approval_rule':
      // Same as `user`: the catalogue is the only screen a rule has (`AM-144` point 5).
      return '/admin/approval-rules';
    case 'automation_rule':
      return '/admin/automation';
    case 'organization':
      // Same as `user`: the catalogue is the screen an organization has.
      return '/admin/organizations';
    case 'asset_type':
      // The two catalogues of the inventory have no card of their own either: the catalogue is the screen.
      return '/admin/asset-types';
    case 'ci_type':
      return '/admin/ci-types';
    // `AM-897` point 5 (migration 0181): each catalogue of the configuration leads to its screen of
    // Administración, as the two above. What is edited inside another screen leads to that one: the
    // impacts, urgencies and matrix of "Impacto, urgencia y prioridad", the reasons of a status of
    // "Estados", and the targets and pause reasons of "Políticas de SLA".
    case 'priority':
    case 'impact':
    case 'urgency':
    case 'priority_matrix':
      return '/admin/priorities';
    case 'category':
      return '/admin/categories';
    case 'status':
    case 'status_reason':
      return '/admin/statuses';
    case 'status_transition':
      return '/admin/transitions';
    case 'task_type':
      return '/admin/task-types';
    case 'work_type':
      return '/admin/work-types';
    case 'tax_rate':
      return '/admin/tax-rates';
    case 'service':
      return '/admin/services';
    case 'service_offering':
      return '/admin/service-offerings';
    case 'service_area':
      return '/admin/service-areas';
    case 'business_hours':
      return '/admin/business-hours';
    case 'department':
      return '/admin/departments';
    case 'location':
      return '/admin/locations';
    case 'vendor':
      return '/admin/vendors';
    case 'team':
      return '/admin/teams';
    case 'agent_profile':
      return '/admin/agent-profiles';
    case 'person':
      return '/admin/persons';
    case 'technician_role':
      return '/admin/technician-roles';
    case 'sla_policy':
    case 'sla_target':
    case 'sla_pause_reason':
      return '/admin/sla-policies';
    case 'notification_rule':
      return '/admin/notification-rules';
    case 'notification_template':
      return '/admin/notifications';
    case 'maintenance_plan':
      return '/admin/maintenance-plans';
    // `request` has no standalone console route (13 §5.4), nor does a configuration item (it lives
    // on the card of its asset); the rest never gained one either.
    default:
      return undefined;
  }
}
